import React, { useCallback, useMemo, useState } from "react";
import { css } from "glamor";
import Spinner from "../library/Spinner";
import AddCardModal from "../subscriptions/AddCardModal";
import ChangeCardModal from "../subscriptions/ChangeCardModal";
import RetryChargeModal from "../subscriptions/RetryChargeModal";
import FinanceHelpSection from "./FinanceHelpSection";
import SubscriptionCard from "./SubscriptionCard";
import PaymentMethodsSection from "./PaymentMethodsSection";

const panelClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  fontFamily: "Montserrat, sans-serif",
  background: "#FFFFFF",
  border: "none",
  borderRadius: 0,
  padding: 0,
  "@media(max-width: 640px)": {
    gap: 10,
    padding: 0,
  },
});

const emptyClass = css({
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  padding: "52px 24px",
  textAlign: "center",
  color: "#64748B",
  fontSize: 14,
  "@media(max-width: 640px)": {
    padding: "36px 16px",
  },
});

export default function FinanceSubscriptionsPanel({
  subscriptions = [],
  creditCardTokens = [],
  currentUser,
  invoices = [],
  loading,
  error,
  paymentMethodLoading = false,
  paymentMethodActionId = null,
  subscriptionActionId = null,
  subscriptionCancelOptions = {},
  onUpdatePaymentMethod = () => Promise.resolve(),
  onSetDefaultPaymentMethod = () => Promise.resolve(),
  onDeletePaymentMethod = () => Promise.resolve(),
  onCancelSubscription = () => Promise.resolve(),
  onRetryCharge = () => Promise.resolve(),
  retryLoading = false,
  onAddCard = () => Promise.resolve(),
  requiresCpf = false,
}) {
  const [changeCardSubscription, setChangeCardSubscription] = useState(null);
  const [addCardSubscription, setAddCardSubscription] = useState(null);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [addCardCameFromChange, setAddCardCameFromChange] = useState(false);
  const [retrySubscription, setRetrySubscription] = useState(null);

  const sortedSubscriptions = useMemo(() => (
    [...subscriptions].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  ), [subscriptions]);

  const latestSubscriptions = useMemo(() => (
    sortedSubscriptions.filter(s => !s.resumedToId)
  ), [sortedSubscriptions]);

  const paymentMethodSubscription = useMemo(() => (
    sortedSubscriptions.find((s) => s.status === "active" && s.paymentMethod === "credit_card") ||
    sortedSubscriptions.find((s) => s.paymentMethod === "credit_card")
  ), [sortedSubscriptions]);

  const changeCardTokens = useMemo(() => {
    if (!changeCardSubscription) return [];
    return creditCardTokens.filter((card) => (
      card.paymentService === changeCardSubscription.paymentService
    ));
  }, [creditCardTokens, changeCardSubscription]);

  const retryTokens = useMemo(() => {
    if (!retrySubscription) return [];
    return creditCardTokens.filter((card) => (
      card.paymentService === retrySubscription.paymentService
    ));
  }, [creditCardTokens, retrySubscription]);

  const handleOpenChangeCard = useCallback((subscription) => {
    setChangeCardSubscription(subscription);
  }, []);

  const handleOpenAddCard = useCallback(() => {
    setAddCardCameFromChange(false);
    setAddCardSubscription(paymentMethodSubscription || sortedSubscriptions[0] || null);
    setAddCardModalOpen(true);
  }, [paymentMethodSubscription, sortedSubscriptions]);

  const handleAddNewFromChange = useCallback(() => {
    setAddCardCameFromChange(true);
    setAddCardSubscription(changeCardSubscription);
    setAddCardModalOpen(true);
    setChangeCardSubscription(null);
  }, [changeCardSubscription]);

  const handleBackToChange = useCallback(() => {
    setChangeCardSubscription(addCardSubscription);
    setAddCardSubscription(null);
    setAddCardCameFromChange(false);
  }, [addCardSubscription]);

  const handleCloseChangeCard = useCallback(() => {
    setChangeCardSubscription(null);
  }, []);

  const handleCloseAddCard = useCallback(() => {
    setAddCardModalOpen(false);
    setAddCardSubscription(null);
    setAddCardCameFromChange(false);
  }, []);

  const handleSubmitChangeCard = useCallback((cardData) => (
    onUpdatePaymentMethod(changeCardSubscription.id, cardData)
  ), [onUpdatePaymentMethod, changeCardSubscription]);

  const handleSubmitAddCard = useCallback((cardData) => (
    addCardSubscription
      ? onUpdatePaymentMethod(addCardSubscription.id, cardData)
      : onAddCard(cardData)
  ), [onUpdatePaymentMethod, onAddCard, addCardSubscription]);

  const handleOpenRetry = useCallback((subscription) => {
    setRetrySubscription(subscription);
  }, []);

  const handleCloseRetry = useCallback(() => {
    setRetrySubscription(null);
  }, []);

  const handleConfirmRetry = useCallback((cardId) => (
    onRetryCharge(retrySubscription.id, cardId).then(() => setRetrySubscription(null))
  ), [onRetryCharge, retrySubscription]);

  if (loading) {
    return <Spinner wrapped text="Carregando assinaturas..." />;
  }

  if (error) {
    return (
      <div className={panelClass}>
        <div className={emptyClass}>
          Houve um erro ao carregar suas assinaturas. Tente novamente mais tarde.
        </div>
        <FinanceHelpSection currentUser={currentUser} />
      </div>
    );
  }

  return (
    <div className={panelClass}>
      {latestSubscriptions.length ? (
        latestSubscriptions.map((subscription) => (
          <SubscriptionCard
            key={subscription.id}
            subscription={subscription}
            invoices={invoices}
            cancelOptions={subscriptionCancelOptions[subscription.id]}
            actionLoading={subscriptionActionId === `cancel-${subscription.id}`}
            onChangePayment={handleOpenChangeCard}
            onCancelSubscription={onCancelSubscription}
            onRetryCharge={() => handleOpenRetry(subscription)}
          />
        ))
      ) : (
        <div className={emptyClass}>
          Você ainda não tem assinaturas.
        </div>
      )}

      <PaymentMethodsSection
        creditCardTokens={creditCardTokens}
        subscriptions={sortedSubscriptions}
        actionId={paymentMethodActionId}
        canAddPaymentMethod
        onAddPaymentMethod={handleOpenAddCard}
        onDeletePaymentMethod={onDeletePaymentMethod}
        onSetDefaultPaymentMethod={onSetDefaultPaymentMethod}
      />
      <FinanceHelpSection currentUser={currentUser} />

      <ChangeCardModal
        open={!!changeCardSubscription}
        onClose={handleCloseChangeCard}
        onSubmit={handleSubmitChangeCard}
        onAddNew={handleAddNewFromChange}
        loading={paymentMethodLoading}
        savedCards={changeCardTokens}
        currentCardLastDigits={changeCardSubscription?.cardLastDigits}
      />

      <AddCardModal
        open={addCardModalOpen}
        onClose={handleCloseAddCard}
        onBack={addCardCameFromChange ? handleBackToChange : undefined}
        onSubmit={handleSubmitAddCard}
        loading={paymentMethodLoading}
        requiresCpf={requiresCpf}
        showSetAsDefault={!addCardSubscription || addCardSubscription.paymentService === 'pagarme'}
      />

      <RetryChargeModal
        open={!!retrySubscription}
        onClose={handleCloseRetry}
        creditCardTokens={retryTokens}
        onConfirm={handleConfirmRetry}
        onNewCardSubmit={onUpdatePaymentMethod}
        loading={retryLoading}
      />
    </div>
  );
}
